import React, { useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { GenericObjectInterface } from "@/src/utilities";
import { useAppDispatch } from "@/src/store/hooks";
import { AppDispatch } from "@/src/store";
import { AsyncThunk } from "@reduxjs/toolkit";
import { fetchSoftwareCategories } from "@/src/store/reducers/ungatedContent/ungatedContentThunk";

interface SubCategory {
  documentId: string;
  name: string;
  contentCount: GenericObjectInterface;
}

export interface Category {
  documentId: string;
  name: string;
  [key: string]: any; // For dynamic subcategory key
}

interface CategoryFilterProps {
  // Data
  categories: Category[];

  // Configuration
  subCategoryKey: string;
  title?: string;

  // State & Callbacks
  selectedCategoryId: string;
  selectedSubCategoryId: string;
  onCategorySelect: (categoryId: string) => void;
  onSubCategorySelect: (subCategoryId: string) => void;

  // Optional
  loading?: boolean;
  className?: string;
  variant?: "vertical" | "horizontal";
  allowCollapse?: boolean;
  parentSection: string;
  showSubcategories?: boolean;

  // Thunk to fetch categories - will be dispatched if categories are empty
  // Defaults to fetchSoftwareCategories if not provided
  fetchCategoriesThunk?: AsyncThunk<GenericObjectInterface, void, any>;
}

function CategorySkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-12 bg-gray-200 rounded-lg animate-pulse" />
      ))}
    </div>
  );
}

export default function CategoryFilter({
  categories,
  subCategoryKey,
  title,
  selectedCategoryId,
  selectedSubCategoryId,
  onCategorySelect,
  onSubCategorySelect,
  loading = false,
  className = "",
  variant = "vertical",
  allowCollapse,
  parentSection,
  showSubcategories = true,
  fetchCategoriesThunk = fetchSoftwareCategories,
}: CategoryFilterProps) {
  const dispatch = useAppDispatch<AppDispatch>();

  // Fetch categories on mount if not already loaded
  useEffect(() => {
    if (categories?.length === 0) {
      dispatch(fetchCategoriesThunk());
    }
  }, [dispatch, categories?.length, fetchCategoriesThunk]);

  // Default allowCollapse based on variant if not explicitly set
  const shouldAllowCollapse = allowCollapse ?? variant === "vertical";

  const handleCategoryClick = (categoryId: string) => {
    // Toggle: if clicking the same category, collapse it (if allowed)
    if (selectedCategoryId === categoryId && shouldAllowCollapse) {
      onCategorySelect("");
    } else {
      onCategorySelect(categoryId);
    }
  };

  const subcategoryList = useMemo(() => {
    if (parentSection) {
      return categories
        ?.find((cat) => cat.documentId === selectedCategoryId)
        ?.[subCategoryKey]?.filter(
          (subCategory: SubCategory) =>
            subCategory?.contentCount?.[parentSection] > 0
        );
    } else {
      return categories?.find((cat) => cat.documentId === selectedCategoryId)?.[
        subCategoryKey
      ];
    }
  }, [categories, selectedCategoryId, parentSection, subCategoryKey]);

  const categoryContentCount = (catId:string) => {
    const categoryObject = categories?.find(
      (cat) => cat?.documentId === catId
    );
    return categoryObject?.subcategories?.reduce((acc:number, currentSubCat:any)=>{
      const currentSubCatTotal = currentSubCat?.contentCount["rss-feed"];
      return currentSubCatTotal + acc
    }, 0)
  };
  // Horizontal Layout
  if (variant === "horizontal") {
    return (
      <div className={`p-4 space-y-4 w-full ${className}`}>
        {title && (
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
        )}
        {/* Top-level categories */}
        <div className="space-y-2 w-full">
          <div className="flex py-2 pr-2 md:p-0 md:flex-wrap overflow-x-auto gap-2 scrollbar-thin scrollbar-thumb-gray-300">
            {loading ? (
              <div className="flex gap-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-8 w-20 bg-gray-200 rounded-full animate-pulse"
                  />
                ))}
              </div>
            ) : (
              categories?.map((category: Category) => (
                <Badge
                  key={category.documentId}
                  onClick={() => handleCategoryClick(category.documentId)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 hover:scale-102 cursor-pointer whitespace-nowrap ${
                    selectedCategoryId === category.documentId
                      ? "shadow-md"
                      : "hover:shadow-sm"
                  }`}
                  variant={
                    selectedCategoryId === category.documentId
                      ? "default"
                      : "outline"
                  }
                >
                  {category.name} {parentSection === "rss-feed" ? `(${categoryContentCount(category.documentId)})` : ""}
                </Badge>
              ))
            )}
          </div>
        </div>

        {/* Subcategories */}
        {showSubcategories &&
          selectedCategoryId &&
          subcategoryList?.length > 0 && (
            <div className="space-y-2 pl-4 border-l-2 border-gray-200 w-full">
              <div className="flex overflow-x-auto md:flex-wrap gap-1.5 scrollbar-thin scrollbar-thumb-gray-300 pb-2">
                {subcategoryList?.map((subCategory: SubCategory) => {
                  return (
                    <Badge
                      key={subCategory.documentId}
                      variant={
                        selectedSubCategoryId === subCategory.documentId
                          ? "secondary"
                          : "outline"
                      }
                      onClick={() =>
                        onSubCategorySelect(subCategory.documentId)
                      }
                      className={`px-3 py-1 text-xs font-medium transition-all duration-200 hover:scale-102 cursor-pointer whitespace-nowrap ${
                        selectedSubCategoryId === subCategory.documentId
                          ? "shadow-sm bg-primary/10"
                          : "hover:shadow-sm"
                      }`}
                    >
                      {subCategory.name}{" "}
                      {subCategory?.contentCount?.[parentSection] &&
                      subCategory?.contentCount?.[parentSection] > 0
                        ? `(${subCategory?.contentCount?.[parentSection]})`
                        : ""}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
      </div>
    );
  }

  // Vertical Layout (default)
  return (
    <aside
      className={`w-[30%] border-gray-200 p-4 md:p-8 flex flex-col ${className}`}
    >
      {title && (
        <h2 className="text-2xl font-bold text-gray-900 mb-8">{title}</h2>
      )}
      <nav className="space-y-3 flex-1 overflow-y-auto divide-y divide-gray-200">
        {loading ? (
          <CategorySkeleton />
        ) : categories?.length > 0 ? (
          categories.map((category: Category) => {
            let subCategories = [];
            if (parentSection) {
              subCategories = category[subCategoryKey]?.filter(
                (subCategory: SubCategory) =>
                  subCategory?.contentCount?.[parentSection] > 0
              ) as SubCategory[];
            } else {
              subCategories = category[subCategoryKey] as SubCategory[];
            }
            const isExpanded = selectedCategoryId === category.documentId;

            return (
              <div key={category.documentId} className="space-y-2">
                {/* Main Category */}
                <button
                  onClick={() => handleCategoryClick(category.documentId)}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-colors ${
                    isExpanded
                      ? "bg-primary/90 text-white hover:bg-primary"
                      : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{category.name}</span>
                    <svg
                      className={`w-5 h-5 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                {/* Subcategories */}
                {showSubcategories &&
                  isExpanded &&
                  subCategories?.length > 0 && (
                    <div className="space-y-1 bg-white p-2 rounded-xl">
                      {subCategories.map((subCategory: SubCategory) => (
                        <button
                          key={subCategory.documentId}
                          onClick={() => {
                            onSubCategorySelect(subCategory.documentId);
                          }}
                          className={`block w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                            subCategory.documentId === selectedSubCategoryId
                              ? "bg-orange-50 text-orange-600 border border-orange-200"
                              : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          {subCategory.name}{" "}
                          {subCategory?.contentCount?.[parentSection] &&
                          subCategory?.contentCount?.[parentSection] > 0
                            ? `(${subCategory?.contentCount?.[parentSection]})`
                            : ""}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            );
          })
        ) : (
          <div className="text-gray-600">No categories available</div>
        )}
      </nav>
    </aside>
  );
}
